import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../api";
import {
  ASSINATURA,
  EMAIL_RH,
  IDEIA,
  SONHO,
  VALORES,
  bannerDoPrograma,
  programaPorKey,
  type CampoPrograma,
} from "../programas";
import { IconCheck, IconClose, IconEye, IconSparkles } from "../icons";
import logoBranca from "../assets/logo-branca.png";

/**
 * PÁGINA PÚBLICA DOS PROGRAMAS — o que o colaborador abre pelo link do RH ou
 * pelo QR Code do cartaz: /programa/<token>.
 *
 * Sem login: o token é a credencial. Uma página só serve aos DOIS programas —
 * o servidor diz qual é (`programa`) e o resto (o texto do material, os campos
 * da ficha) vem de src/programas.ts. Isso evita duas telas quase iguais que
 * divergem na primeira correção.
 *
 * O material impresso é reproduzido aqui de propósito: quem recebe o link no
 * WhatsApp não tem o cartaz na mão, e as regras (ou as categorias, e os
 * prêmios) são o que fazem a pessoa decidir se participa e o que escrever.
 */

/* ------------------------------------------------------------------ *
 *  Um campo da ficha                                                  *
 * ------------------------------------------------------------------ *
 * Declarado FORA do componente: componente aninhado é remontado a cada
 * tecla, o campo perderia o foco a cada letra digitada.                */

function Campo({
  campo,
  valor,
  onChange,
}: {
  campo: CampoPrograma;
  valor: string;
  onChange: (v: string) => void;
}) {
  const comum = {
    value: valor,
    onChange: (e: { target: { value: string } }) => onChange(e.target.value),
  };
  return (
    <label className={campo.largo ? "field field-wide" : "field"}>
      <span>
        {campo.label}
        {campo.obrigatorio && <i className="req"> *</i>}
      </span>
      {campo.tipo === "textarea" ? (
        <textarea rows={4} {...comum} />
      ) : campo.tipo === "select" ? (
        <select {...comum}>
          <option value="">Escolha…</option>
          {(campo.opcoes ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : (
        <input
          {...comum}
          inputMode={campo.tipo === "currency" ? "numeric" : undefined}
          placeholder={
            campo.tipo === "tel" ? "(19) 90000-0000" : campo.tipo === "currency" ? "R$ 0,00" : undefined
          }
        />
      )}
      {campo.dica && <small className="hint">{campo.dica}</small>}
    </label>
  );
}

/**
 * O cartaz em tela cheia. No celular o banner fica pequeno para ler (é um
 * cartaz 16:9 cheio de texto), então aqui ele abre no tamanho real e dá para
 * arrastar para os lados. Esc ou toque fora fecha.
 */
function CartazAmpliado({ src, alt, onFechar }: { src: string; alt: string; onFechar: () => void }) {
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => e.key === "Escape" && onFechar();
    window.addEventListener("keydown", tecla);
    const antes = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", tecla);
      document.body.style.overflow = antes;
    };
  }, [onFechar]);

  return (
    <div className="prog-zoom" role="dialog" aria-modal="true" aria-label={alt} onClick={onFechar}>
      <button type="button" className="prog-zoom-fechar" onClick={onFechar} aria-label="Fechar">
        <IconClose size={20} />
      </button>
      <div className="prog-zoom-area" onClick={(e) => e.stopPropagation()}>
        <img src={src} alt={alt} />
      </div>
    </div>
  );
}

/** As listas do material (regras, critérios, exemplos, categorias, prêmios). */
function Bloco({ titulo, itens }: { titulo: string; itens: readonly string[] }) {
  return (
    <div className="prog-bloco">
      <h3>{titulo}</h3>
      <ul>
        {itens.map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ul>
    </div>
  );
}

interface Props {
  token: string;
}

export default function ProgramaPublica({ token }: Props) {
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [campanha, setCampanha] = useState<{
    programa: string;
    campanha: string;
    descricao: string | null;
  } | null>(null);

  const [valores, setValores] = useState<Record<string, string>>({});
  const [aceite, setAceite] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState<string | null>(null);
  const [erroForm, setErroForm] = useState<string | null>(null);
  const [zoom, setZoom] = useState(false);
  const fecharZoom = useCallback(() => setZoom(false), []);

  // O botão flutuante "Quero participar" (celular) some quando a ficha já está
  // na tela — senão ele fica por cima dos campos que a pessoa está preenchendo.
  const fichaRef = useRef<HTMLFormElement | null>(null);
  const [fichaVisivel, setFichaVisivel] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setCampanha(await api.programas.formulario(token));
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não consegui abrir este link.");
    } finally {
      setCarregando(false);
    }
  }, [token]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const prog = useMemo(() => programaPorKey(campanha?.programa ?? "sonho"), [campanha?.programa]);
  const ehSonho = prog.key === "sonho";
  const banner = bannerDoPrograma(prog.key);
  const textoCta = ehSonho ? "Quero me inscrever" : "Quero enviar minha ideia";

  useEffect(() => {
    const el = fichaRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(([e]) => setFichaVisivel(e.isIntersecting), {
      threshold: 0.05,
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [campanha, enviado]);

  function irParaFicha(e: React.MouseEvent) {
    e.preventDefault();
    fichaRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function definir(nome: string, v: string) {
    setValores((prev) => ({ ...prev, [nome]: v }));
  }

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setErroForm(null);

    // As checagens que a pessoa consegue corrigir na hora ficam aqui; o
    // servidor confere tudo de novo (é ele quem decide).
    const faltando = prog.campos.filter((c) => c.obrigatorio && !(valores[c.nome] ?? "").trim());
    if (faltando.length) {
      setErroForm(`Preencha: ${faltando.map((c) => c.label).join(", ")}.`);
      return;
    }
    if (ehSonho && !aceite) {
      setErroForm("É preciso concordar com as regras do programa.");
      return;
    }

    setEnviando(true);
    try {
      const r = await api.programas.responder(token, { ...valores, aceite: ehSonho ? aceite : undefined });
      setEnviado(r.mensagem);
    } catch (err) {
      setErroForm(err instanceof ApiError ? err.message : "Não consegui enviar. Tente de novo.");
    } finally {
      setEnviando(false);
    }
  }

  if (carregando) {
    return (
      <div className="aval-publica">
        <div className="aval-card">Carregando…</div>
      </div>
    );
  }

  if (erro || !campanha) {
    return (
      <div className="aval-publica">
        <div className="aval-header">
          <div className="aval-brand">SERCAMP</div>
          <div className="aval-brand-sub">Programas internos</div>
        </div>
        <div className="aval-card aval-estado">
          <p>{erro ?? "Link indisponível."}</p>
          <small className="muted">
            Se você recebeu este link do RH, peça um novo — ou escreva para {EMAIL_RH}.
          </small>
        </div>
      </div>
    );
  }

  /* A barra do topo, igual nas duas telas (página e "enviado"). */
  const topo = (
    <header className="prog-lp-topo">
      <img src={logoBranca} alt="SERCAMP" className="prog-lp-logo" />
      <span className="prog-lp-topo-txt">
        <strong>{prog.nome}</strong>
        <small>{campanha.campanha}</small>
      </span>
    </header>
  );

  if (enviado) {
    return (
      <div className="aval-publica prog-lp">
        {topo}
        <figure className="prog-hero prog-hero-so-img">
          <img src={banner} alt={`Cartaz do programa ${prog.nome}`} />
        </figure>
        <div className="aval-card aval-estado">
          <div className="aval-ok-icon">
            <IconCheck size={30} />
          </div>
          <p>{enviado}</p>
          <small className="muted">Pode fechar esta página.</small>
        </div>
        <p className="prog-assinatura">{ASSINATURA}</p>
      </div>
    );
  }

  return (
    <div className="aval-publica prog-lp">
      {topo}

      {/* ---- O BANNER: o cartaz oficial, com a chamada logo embaixo ---- */}
      <section className="prog-hero">
        <button
          type="button"
          className="prog-hero-img"
          onClick={() => setZoom(true)}
          aria-label="Ver o cartaz em tela cheia"
        >
          <img src={banner} alt={`Cartaz do programa ${prog.nome}`} />
          <span className="prog-hero-ampliar">
            <IconEye size={14} /> Ver cartaz ampliado
          </span>
        </button>

        <div className="prog-hero-faixa">
          <div className="prog-hero-texto">
            <span className="prog-selo">
              <i aria-hidden="true" />
              {ehSonho ? "Inscrições abertas" : "Campanha aberta"}
            </span>
            <h1>{prog.chamada}</h1>
            <p>{campanha.descricao ?? prog.descricao}</p>
          </div>
          <a href="#ficha" className="prog-hero-btn" onClick={irParaFicha}>
            {textoCta} <span aria-hidden="true">→</span>
          </a>
        </div>
      </section>

      {/* ---- O material: por que o programa existe ---- */}
      <div className="aval-card prog-intro">
        <p className="prog-frase">
          <IconSparkles size={15} /> {prog.frase}
        </p>

        {ehSonho ? (
          <div className="prog-destaques">
            {SONHO.destaques.map((d) => (
              <div key={d.titulo}>
                <strong>{d.titulo}</strong>
                <small>{d.texto}</small>
              </div>
            ))}
          </div>
        ) : (
          <div className="prog-destaques">
            {IDEIA.premios.map((p) => (
              <div key={p.titulo}>
                <strong>{p.titulo}</strong>
                <small>{p.texto}</small>
              </div>
            ))}
          </div>
        )}

        <ol className="prog-passos">
          {prog.comoFunciona.map((p, i) => (
            <li key={p.titulo}>
              <span className="prog-num">{i + 1}</span>
              <div>
                <strong>{p.titulo}</strong>
                <small>{p.texto}</small>
              </div>
            </li>
          ))}
        </ol>

        {ehSonho ? (
          <>
            <Bloco titulo="Regras do programa" itens={SONHO.regras} />
            <Bloco titulo="Critérios de seleção" itens={SONHO.criterios} />
            <Bloco titulo="Exemplos de sonhos" itens={SONHO.exemplos} />
            <p className="prog-edicoes">{SONHO.edicoes.join(" · ")}</p>
          </>
        ) : (
          <Bloco titulo="Que tipo de ideia você pode enviar?" itens={IDEIA.categorias} />
        )}

        <blockquote className="prog-citacao">{prog.citacao}</blockquote>
      </div>

      {/* ---- A ficha ---- */}
      <form id="ficha" ref={fichaRef} className="aval-card prog-ficha" onSubmit={enviar}>
        <h3 className="prog-ficha-titulo">
          {ehSonho ? "Ficha de inscrição" : "Envie a sua ideia"}
        </h3>

        <div className="form-grid">
          {prog.campos.map((c) => (
            <Campo
              key={c.nome}
              campo={c}
              valor={valores[c.nome] ?? ""}
              onChange={(v) => definir(c.nome, v)}
            />
          ))}
        </div>

        {ehSonho && (
          <label className="prog-aceite">
            <input type="checkbox" checked={aceite} onChange={(e) => setAceite(e.target.checked)} />
            <span>{SONHO.aceite}</span>
          </label>
        )}

        {erroForm && <div className="form-error">{erroForm}</div>}

        <button type="submit" className="btn-primary prog-enviar" disabled={enviando}>
          {enviando ? "Enviando…" : ehSonho ? "Enviar minha inscrição" : "Enviar minha ideia"}
        </button>

        <small className="muted prog-rodape">
          Dúvidas? Fale com o RH: {EMAIL_RH} · {VALORES.join(" · ")}
        </small>
      </form>

      <p className="prog-assinatura">{ASSINATURA}</p>

      {/* Celular: o botão acompanha a rolagem até a ficha aparecer. */}
      {!fichaVisivel && (
        <a href="#ficha" className="prog-cta-flutuante" onClick={irParaFicha}>
          {textoCta}
        </a>
      )}

      {zoom && (
        <CartazAmpliado src={banner} alt={`Cartaz do programa ${prog.nome}`} onFechar={fecharZoom} />
      )}
    </div>
  );
}
